import type {
  GetTokenUnlocksRequest,
  TokenEmissionPoint,
  TokenEmissionSeries,
  TokenUnlockAllocation,
  TokenUnlockEvent,
} from '@/types/generated/public_api'

export type { TokenEmissionPoint, TokenEmissionSeries, TokenUnlockAllocation, TokenUnlockEvent }

/**
 * Request shape for `fetchTokenUnlocks` — a plain one-shot fetch, so `offset` is
 * still a caller-supplied field here. `useTokenUnlocksInfinite` (tokenomist.hook.ts)
 * accepts `Omit<TokenUnlockHistoryParams, 'offset'>` instead, since pagination there
 * is owned end-to-end by `useInfiniteQuery`'s `pageParam`.
 */
export type TokenUnlockHistoryParams = GetTokenUnlocksRequest

/**
 * `value_to_market_cap` is a PERCENT already (11.6 means 11.6% of market cap), not
 * a fraction — never divide by 100 before formatting.
 */
export function formatValueToMarketCap(pct: number): string {
  return `${pct.toFixed(1)}%`
}

/**
 * The six vendor buckets confirmed today. `standardAllocationName` is documented as
 * a free string the vendor can extend without notice ("Good enough to colour a
 * stacked bar" — not a closed enum), so callers must go through `getAllocationColor`
 * rather than indexing this map directly, to avoid a silently-missing color for an
 * unrecognized bucket.
 */
export const STANDARD_ALLOCATION_COLORS: Record<string, string> = {
  Community: '#4ADE80',
  'Founder / Team': '#F87171',
  Others: '#A78BFA',
  'Private Investors': '#FBBF24',
  'Public Investors': '#38BDF8',
  Reserve: '#94A3B8',
}

export const DEFAULT_ALLOCATION_COLOR = '#6B7280'

export function getAllocationColor(name: string): string {
  return STANDARD_ALLOCATION_COLORS[name] ?? DEFAULT_ALLOCATION_COLOR
}

const MS_PER_DAY = 86_400_000
const NEAR_DATED_WINDOW_MS = 14 * MS_PER_DAY

/** Precisions coarser than 'day' — an unlock date at this precision is an estimate. */
const COARSE_PRECISIONS = new Set(['week', 'month', 'year', 'unknown', ''])

function parseMs(value: string | undefined): number | null {
  if (value === undefined) return null
  const ms = Number(value)
  return Number.isFinite(ms) && ms > 0 ? ms : null
}

/**
 * Whether an unlock's date should be shown as an estimate rather than an
 * appointment. Two independent triggers, either one is sufficient:
 *
 * - Any allocation breakdown entry carries a precision coarser than 'day'
 *   (including 'unknown' and '' — both occur live, and 'unknown' means the
 *   vendor's AI listed the token rather than reading a whitepaper).
 * - The unlock date falls inside the next 14 days: vendor policy withholds
 *   privately-sourced schedule changes made inside 14 days of an unlock and
 *   publishes them 7-14 days AFTER the event, so a near-dated unlock is the
 *   LEAST reliable, not the most.
 *
 * `nowMs` is a parameter, not read internally, so the 14-day check is
 * deterministic under test.
 */
export function eventPrecisionIsApproximate(
  event: Pick<TokenUnlockEvent, 'unlockDate' | 'allocationBreakdown'>,
  nowMs: number = Date.now()
): boolean {
  const coarsePrecision = event.allocationBreakdown.some((allocation) =>
    COARSE_PRECISIONS.has(allocation.unlockPrecision)
  )
  if (coarsePrecision) return true

  const unlockMs = parseMs(event.unlockDate)
  if (unlockMs === null) return false
  return unlockMs >= nowMs && unlockMs <= nowMs + NEAR_DATED_WINDOW_MS
}
